import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
  getTrendingDestinationsCacheKey,
  getTrendingDestinationsForFilter,
  TrendingDestination,
} from 'home/components/TrendingDestinations/constants'
import { fetchTrendingDestinations } from 'actions/DestinationAlertsActions'
import { buildHotelUrl } from 'home/components/TrendingDestinations/utils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { DESTINATIONS } from 'constants/config/region'

// Tourism board partnerships - hardcoded positions for AU region
const WESTERN_AUSTRALIA_TRENDING_DESTINATION: TrendingDestination = {
  name: DESTINATIONS.western_australia.displayName,
  destinationId: DESTINATIONS.western_australia.destinationId,
  imageId: 'sxvcf0ihy2fyxf4s5dz',
  url: buildHotelUrl(DESTINATIONS.western_australia.displayName, DESTINATIONS.western_australia.destinationId),
}
const WESTERN_AUSTRALIA_POSITION_AU = 3 // 0-indexed, so position 4 is index 3

const CALIFORNIA_TRENDING_DESTINATION: TrendingDestination = {
  name: DESTINATIONS.california.displayName,
  destinationId: DESTINATIONS.california.destinationId,
  imageId: 'mehojhk7whqysib4n23',
  url: buildHotelUrl(DESTINATIONS.california.displayName, DESTINATIONS.california.destinationId),
}
const CALIFORNIA_POSITION_AU = 4 // 0-indexed, so position 5 is index 4

export function useHotelTrendingDestinations(
  selectedFilter: App.DestinationSelectedFilters,
  currentRegionCode: string,
  leUserId?: string,
  domainUserId?: string,
  disablePersonalisation?: boolean,
): [Array<TrendingDestination>, boolean] {
  const dispatch = useAppDispatch()

  // Create the same cache key used by the action
  const userKey = getTrendingDestinationsCacheKey(leUserId, domainUserId, disablePersonalisation)

  useEffect(() => {
    dispatch(fetchTrendingDestinations(leUserId, domainUserId, disablePersonalisation))
  }, [dispatch, leUserId, domainUserId, disablePersonalisation])

  const trendingDestinations = useAppSelector(
    state => state.destination?.trendingDestinationsByUserKey?.[userKey],
  ) ?? EmptyArray

  const fetching = useAppSelector(
    state => state.destination?.fetchingTrendingDestinationsByUserKey?.[userKey] ?? false,
  )

  const displayTrending: Array<TrendingDestination> = useMemo(() => {
    if (fetching) return []

    const destinations = trendingDestinations
      .filter(destination => destination.imageId)
      .map(destination => ({
        name: destination.primaryText,
        imageId: destination.imageId,
        url: buildHotelUrl(destination.primaryText, destination.placeId),
        destinationId: destination.placeId,
      } as TrendingDestination))

    // Tourism board partnerships - insert hardcoded destinations for AU region
    // Remove if backend returned them, then insert at fixed positions
    if (currentRegionCode === 'AU' && destinations.length >= CALIFORNIA_POSITION_AU) {
      const filteredDestinations = destinations.filter(
        d => d.destinationId !== WESTERN_AUSTRALIA_TRENDING_DESTINATION.destinationId &&
          d.destinationId !== CALIFORNIA_TRENDING_DESTINATION.destinationId,
      )
      filteredDestinations.splice(WESTERN_AUSTRALIA_POSITION_AU, 0, WESTERN_AUSTRALIA_TRENDING_DESTINATION)
      filteredDestinations.splice(CALIFORNIA_POSITION_AU, 0, CALIFORNIA_TRENDING_DESTINATION)
      return filteredDestinations
    }

    return destinations
  }, [trendingDestinations, fetching, currentRegionCode])

  if (selectedFilter !== 'hotel') {
    const hardCodedTrendingDestinations = getTrendingDestinationsForFilter(selectedFilter, currentRegionCode) ?? []
    return [hardCodedTrendingDestinations, false]
  }

  return [displayTrending, fetching]
}
